'use client';

import { c as _c } from "react/compiler-runtime";
import type { DirectPurchaseBinding, FundingQuoteReview } from '@morfi/onramp-sdk';
import { type AmountInputCodec, createAmountInputCodec, decimalToUnits, type FundingFeeEstimate, type FundingLimit, type FundingMethod, formatFiatUnits, formatFundingPreset, fundingMethodArtwork, MoneyError } from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedAmountInput } from '@/components/trade/animated-amount-input';
import { AnimatedButton } from '@/components/ui/animated-button';
import { ArrowDown01Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { currencyParts } from '@/lib/currency/index';
import { showToast } from '@/lib/ui/toast';
import { FundingFees } from './funding-fees';
export interface FundingAmountProps {
  method?: FundingMethod;
  methodLabel?: string;
  methodArtwork?: ReactNode;
  feeDisclosure?: ReactNode;
  currencySymbol?: string;
  presets: readonly string[];
  presetLabel?(units: string): string;
  currency: string;
  decimals: number;
  estimate: FundingFeeEstimate | null;
  purchase?: DirectPurchaseBinding;
  purchaseReview?: FundingQuoteReview | null;
  limits?: readonly FundingLimit[];
  recovery?: ReactNode;
  primaryAction?: ReactNode;
  quoteLoading?: boolean;
  allowanceBlocked?: boolean;
  boundaryFeedback?: 'snackbar';
  amountBoundary?: {
    kind: 'minimum' | 'maximum';
    amountUnits: string;
  } | null;
  validationError?: 'payment_invalid_request' | 'quote_not_executable' | null;
  value: string;
  canContinue: boolean;
  pending?: boolean;
  onAmountChange?(units: string | null): void;
  onValueChange(value: string): void;
  onChangeMethod(): void;
  onContinue(units: string): void;
}
const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'decimal', '0', 'delete'] as const;

/** Amount entry only. The host advances to contact/quote review, never directly submits a payment. */
export function FundingAmount(t0) {
  const $ = _c(136);
  const {
    method,
    methodLabel,
    methodArtwork,
    feeDisclosure,
    currencySymbol,
    presets,
    presetLabel,
    currency,
    decimals,
    estimate,
    purchase,
    purchaseReview,
    value: text,
    limits,
    recovery,
    primaryAction,
    quoteLoading,
    allowanceBlocked,
    amountBoundary,
    boundaryFeedback,
    validationError,
    canContinue,
    pending: t1,
    onAmountChange,
    onValueChange,
    onChangeMethod,
    onContinue
  } = t0;
  const pending = t1 === undefined ? false : t1;
  const t = useTranslations("onramp");
  const quoteStatus = useTranslations("trade.status");
  const locale = useLocale();
  const codec = createAmountInputCodec(locale, decimals);
  const [touched, setTouched] = useState(false);
  const units = readUnits(codec, text);
  let symbol;
  let t2;
  let t3;
  if ($[0] !== currency || $[1] !== currencySymbol || $[2] !== locale) {
    const parts = currencyParts(locale, currency);
    symbol = currencySymbol ?? parts.find(_temp)?.value ?? currency;
    t2 = parts.findIndex(_temp2);
    t3 = parts.findIndex(_temp3);
    $[0] = currency;
    $[1] = currencySymbol;
    $[2] = locale;
    $[3] = symbol;
    $[4] = t2;
    $[5] = t3;
  } else {
    symbol = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  const suffix = t2 > t3;
  const valid = units !== null && BigInt(units) > BigInt(0);
  const primaryUnavailable = !valid || Boolean(amountBoundary || validationError || quoteLoading);
  const validationMessage = validationError ?? (touched && text !== "" && !valid ? "invalid_amount" : null);
  const announcedError = useRef(null);
  let t4;
  let t5;
  if ($[6] !== t || $[7] !== validationMessage) {
    t4 = () => {
      if (validationMessage && announcedError.current !== validationMessage) {
        showToast({
          id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
          message: validationMessage === "invalid_amount" ? t("invalid_amount") : t(validationMessage),
          variant: "error"
        });
      }
      announcedError.current = validationMessage;
    };
    t5 = [validationMessage, t];
    $[6] = t;
    $[7] = validationMessage;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const boundaryKind = amountBoundary?.kind;
  const boundaryUnits = amountBoundary?.amountUnits;
  let t6;
  let t7;
  if ($[10] !== boundaryFeedback || $[11] !== boundaryKind || $[12] !== boundaryUnits || $[13] !== currency || $[14] !== decimals || $[15] !== locale || $[16] !== t) {
    t6 = () => {
      if (boundaryFeedback !== "snackbar" || !boundaryKind || boundaryUnits === undefined) {
        return;
      }
      const timer = setTimeout(() => showToast({
        id: TOAST_IDS.ONRAMP_AMOUNT_ERROR,
        message: t(boundaryKind === "minimum" ? "amount_minimum" : "amount_maximum", {
          amount: formatFiatUnits(boundaryUnits, decimals, currency, locale)
        }),
        variant: "error"
      }), 800);
      return () => clearTimeout(timer);
    };
    t7 = [boundaryFeedback, boundaryKind, boundaryUnits, currency, decimals, locale, t];
    $[10] = boundaryFeedback;
    $[11] = boundaryKind;
    $[12] = boundaryUnits;
    $[13] = currency;
    $[14] = decimals;
    $[15] = locale;
    $[16] = t;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  if ($[19] !== codec || $[20] !== onAmountChange || $[21] !== onValueChange) {
    t8 = function change(value) {
      onValueChange(value);
      onAmountChange?.(readUnits(codec, value));
    };
    $[19] = codec;
    $[20] = onAmountChange;
    $[21] = onValueChange;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const change = t8;
  let t9;
  if ($[23] !== amountBoundary || $[24] !== canContinue || $[25] !== onContinue || $[26] !== pending || $[27] !== quoteLoading || $[28] !== setTouched || $[29] !== units || $[30] !== valid || $[31] !== validationError) {
    t9 = function submit() {
      setTouched(true);
      if (valid && units !== null && canContinue && !pending && !quoteLoading && !validationError && !amountBoundary) {
        onContinue(units);
      }
    };
    $[23] = amountBoundary;
    $[24] = canContinue;
    $[25] = onContinue;
    $[26] = pending;
    $[27] = quoteLoading;
    $[28] = setTouched;
    $[29] = units;
    $[30] = valid;
    $[31] = validationError;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const submit = t9;
  let t10;
  if ($[33] !== method || $[34] !== methodArtwork) {
    t10 = methodArtwork ?? (method ? <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={`/assets/onramp/method-${fundingMethodArtwork[method]}.png`} alt="" size="size-5" className="bg-transparent" /> : null);
    $[33] = method;
    $[34] = methodArtwork;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== method || $[37] !== methodLabel || $[38] !== t) {
    t11 = methodLabel ?? (method ? t(method) : "");
    $[36] = method;
    $[37] = methodLabel;
    $[38] = t;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== onChangeMethod || $[41] !== pending || $[42] !== t10 || $[43] !== t11) {
    t12 = <AnimatedButton type="button" variant="secondary" className="h-auto self-center gap-2 rounded-full px-3 py-1 text-caption font-medium text-foreground-primary" suffix={ArrowDown01Icon} onClick={onChangeMethod} disabled={pending}>{t10}{t11}</AnimatedButton>;
    $[40] = onChangeMethod;
    $[41] = pending;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t) {
    t13 = t("principal");
    $[45] = t;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const t14 = suffix ? "" : symbol;
  const t15 = suffix ? symbol : "";
  let t16;
  if ($[47] !== setTouched) {
    t16 = () => setTouched(true);
    $[47] = setTouched;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const t17 = Boolean(validationError) || touched && text !== "" && !valid;
  let t18;
  if ($[49] !== change || $[50] !== pending || $[51] !== submit || $[52] !== t13 || $[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t17 || $[57] !== text) {
    t18 = <div dir="ltr"><AnimatedAmountInput inputLabel={t13} currencySymbol={t14} suffixSymbol={t15} localizedInput={true} showQuickAmounts={false} inputMode="none" value={text} disabled={pending} onChange={change} onBlur={t16} onSubmit={submit} ariaInvalid={t17} /></div>;
    $[49] = change;
    $[50] = pending;
    $[51] = submit;
    $[52] = t13;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = text;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== change || $[60] !== codec || $[61] !== currency || $[62] !== decimals || $[63] !== locale || $[64] !== pending || $[65] !== presetLabel || $[66] !== presets) {
    let t20;
    if ($[68] !== change || $[69] !== codec || $[70] !== currency || $[71] !== decimals || $[72] !== locale || $[73] !== pending || $[74] !== presetLabel) {
      t20 = value_0 => <AnimatedButton key={value_0} type="button" variant="secondary" size="sm" className="rounded-full" disabled={pending} onClick={() => change(codec.format(BigInt(value_0)))}>{presetLabel?.(value_0) ?? formatFundingPreset(value_0, decimals, currency, locale)}</AnimatedButton>;
      $[68] = change;
      $[69] = codec;
      $[70] = currency;
      $[71] = decimals;
      $[72] = locale;
      $[73] = pending;
      $[74] = presetLabel;
      $[75] = t20;
    } else {
      t20 = $[75];
    }
    t19 = presets.map(t20);
    $[59] = change;
    $[60] = codec;
    $[61] = currency;
    $[62] = decimals;
    $[63] = locale;
    $[64] = pending;
    $[65] = presetLabel;
    $[66] = presets;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[76] !== t19) {
    t20 = <div className="flex flex-wrap justify-center gap-2">{t19}</div>;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  if ($[78] !== t) {
    t21 = t("amount_keypad");
    $[78] = t;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== change || $[81] !== codec || $[82] !== decimals || $[83] !== pending || $[84] !== t || $[85] !== text) {
    t22 = keys.map(key => <AnimatedButton key={key} type="button" variant="tertiary" className="h-12 sm:h-13 text-heading-lg font-medium text-foreground-primary" disabled={pending || key === "decimal" && decimals === 0} aria-label={key === "delete" ? t("delete_digit") : key === "decimal" ? t("decimal_point") : t("digit", {
      digit: key
    })} onClick={() => change(codec.key(text, key))}>{key === "delete" ? "\u232B" : key === "decimal" ? codec.decimalSeparator : codec.format(decimalToUnits(key, decimals))}</AnimatedButton>);
    $[80] = change;
    $[81] = codec;
    $[82] = decimals;
    $[83] = pending;
    $[84] = t;
    $[85] = text;
    $[86] = t22;
  } else {
    t22 = $[86];
  }
  let t23;
  if ($[87] !== t21 || $[88] !== t22) {
    t23 = <fieldset aria-label={t21} className="grid grid-cols-3 gap-0 my-1">{t22}</fieldset>;
    $[87] = t21;
    $[88] = t22;
    $[89] = t23;
  } else {
    t23 = $[89];
  }
  let t24;
  if ($[90] !== currency || $[91] !== decimals || $[92] !== estimate || $[93] !== feeDisclosure || $[94] !== limits || $[95] !== purchase || $[96] !== purchaseReview || $[97] !== quoteLoading || $[98] !== units) {
    t24 = feeDisclosure ?? <FundingFees currency={currency} decimals={decimals} principalUnits={units} estimate={estimate} {...purchase ? {
      purchase,
      purchaseReview
    } : {}} {...limits ? {
      limits
    } : {}} {...quoteLoading !== undefined ? {
      loading: quoteLoading
    } : {}} />;
    $[90] = currency;
    $[91] = decimals;
    $[92] = estimate;
    $[93] = feeDisclosure;
    $[94] = limits;
    $[95] = purchase;
    $[96] = purchaseReview;
    $[97] = quoteLoading;
    $[98] = units;
    $[99] = t24;
  } else {
    t24 = $[99];
  }
  let t25;
  if ($[100] !== allowanceBlocked || $[101] !== amountBoundary || $[102] !== boundaryFeedback || $[103] !== canContinue || $[104] !== currency || $[105] !== decimals || $[106] !== locale || $[107] !== pending || $[108] !== primaryAction || $[109] !== primaryUnavailable || $[110] !== quoteLoading || $[111] !== quoteStatus || $[112] !== submit || $[113] !== t || $[114] !== valid || $[115] !== validationError) {
    t25 = !primaryAction || primaryUnavailable ? <AnimatedButton type="button" className="h-11 w-full" size="lg" disabled={Boolean(allowanceBlocked) || Boolean(amountBoundary) || !valid || !canContinue || Boolean(validationError)} loading={pending || quoteLoading} onClick={submit}>{allowanceBlocked ? t("amount_over_limit") : boundaryFeedback !== "snackbar" && amountBoundary?.kind === "minimum" ? t("below_minimum", {
        amount: formatFiatUnits(amountBoundary.amountUnits, decimals, currency, locale)
      }) : boundaryFeedback !== "snackbar" && amountBoundary?.kind === "maximum" ? t("above_maximum", {
        amount: formatFiatUnits(amountBoundary.amountUnits, decimals, currency, locale)
      }) : quoteLoading ? quoteStatus("loading_quote") : !valid ? t("enter_amount") : canContinue ? t("continue") : t("review_amount")}</AnimatedButton> : null;
    $[100] = allowanceBlocked;
    $[101] = amountBoundary;
    $[102] = boundaryFeedback;
    $[103] = canContinue;
    $[104] = currency;
    $[105] = decimals;
    $[106] = locale;
    $[107] = pending;
    $[108] = primaryAction;
    $[109] = primaryUnavailable;
    $[110] = quoteLoading;
    $[111] = quoteStatus;
    $[112] = submit;
    $[113] = t;
    $[114] = valid;
    $[115] = validationError;
    $[116] = t25;
  } else {
    t25 = $[116];
  }
  const t26 = primaryUnavailable ? "absolute inset-0 opacity-0 pointer-events-none" : undefined;
  let t27;
  if ($[117] !== primaryAction || $[118] !== primaryUnavailable || $[119] !== t26) {
    t27 = <div className={t26} inert={primaryUnavailable}>{primaryAction}</div>;
    $[117] = primaryAction;
    $[118] = primaryUnavailable;
    $[119] = t26;
    $[120] = t27;
  } else {
    t27 = $[120];
  }
  let t28;
  if ($[121] !== t25 || $[122] !== t27) {
    t28 = <div className="relative">{t25}{t27}</div>;
    $[121] = t25;
    $[122] = t27;
    $[123] = t28;
  } else {
    t28 = $[123];
  }
  let t29;
  if ($[124] !== method || $[125] !== t) {
    t29 = method === "apple_pay" ? <p className="text-center text-caption text-foreground-primary">{t("apple_debit_tip")}</p> : null;
    $[124] = method;
    $[125] = t;
    $[126] = t29;
  } else {
    t29 = $[126];
  }
  let t30;
  if ($[127] !== recovery || $[128] !== t12 || $[129] !== t18 || $[130] !== t20 || $[131] !== t23 || $[132] !== t24 || $[133] !== t28 || $[134] !== t29) {
    t30 = <div className="flex flex-col gap-3">{t12}{t18}{t20}{t23}{t24}{recovery}{t28}{t29}</div>;
    $[127] = recovery;
    $[128] = t12;
    $[129] = t18;
    $[130] = t20;
    $[131] = t23;
    $[132] = t24;
    $[133] = t28;
    $[134] = t29;
    $[135] = t30;
  } else {
    t30 = $[135];
  }
  return t30;
}
function _temp3(part_1) {
  return part_1.type === "integer";
}
function _temp2(part_0) {
  return part_0.type === "currency";
}
function _temp(part) {
  return part.type === "currency";
}
function readUnits(codec: AmountInputCodec, value: string): string | null {
  try {
    return codec.parse(value, BigInt('99999999999999999999')).toString();
  } catch (error) {
    // SILENT-CATCH: Incomplete or invalid typed amounts use validation feedback, not operational failures.
    if (error instanceof MoneyError) return null;
    throw error;
  }
}